import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type CommissioningPhase =
  | 'exploring_subject'
  | 'clarifying_lens'
  | 'triangulating_voice'
  | 'seeding_sources'
  | 'proposing'
  | 'contracted'
  | 'abandoned'

export interface CommissioningMessage {
  id: string
  role: 'user' | 'studio'
  content: string
  // True while this message is still streaming tokens.
  streaming?: boolean
}

interface AuthorCommissioningState {
  sessionId: string | null
  phase: CommissioningPhase
  messages: CommissioningMessage[]
  isStreaming: boolean
  isThinking: boolean
  thinkingText: string
  proposalText: string | null
  authorId: string | null
  error: string | null

  // Actions
  setSessionId: (id: string) => void
  setPhase: (phase: CommissioningPhase) => void
  startUserTurn: (content: string) => void
  startStudioTurn: () => string // returns the new message id
  appendStudioText: (id: string, delta: string) => void
  finishStudioTurn: (id: string, finalContent?: string) => void
  setThinking: (active: boolean) => void
  appendThinkingDelta: (delta: string) => void
  clearThinking: () => void
  setStreaming: (active: boolean) => void
  setProposalText: (text: string | null) => void
  setAuthorId: (id: string | null) => void
  setError: (err: string | null) => void
  reset: () => void
}

function genId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export const useAuthorCommissioningStore = create<AuthorCommissioningState>()(
  persist(
    (set) => ({
      sessionId: null,
      phase: 'exploring_subject',
      messages: [],
      isStreaming: false,
      isThinking: false,
      thinkingText: '',
      proposalText: null,
      authorId: null,
      error: null,

      setSessionId: (id) => set({ sessionId: id }),
      setPhase: (phase) => set({ phase }),

      startUserTurn: (content) =>
        set((s) => ({
          messages: [
            ...s.messages,
            { id: genId(), role: 'user', content },
          ],
          error: null,
        })),

      startStudioTurn: () => {
        const id = genId()
        set((s) => ({
          messages: [
            ...s.messages,
            { id, role: 'studio', content: '', streaming: true },
          ],
        }))
        return id
      },

      appendStudioText: (id, delta) =>
        set((s) => ({
          messages: s.messages.map((m) =>
            m.id === id ? { ...m, content: m.content + delta } : m,
          ),
        })),

      finishStudioTurn: (id, finalContent) =>
        set((s) => ({
          messages: s.messages.map((m) =>
            m.id === id
              ? {
                  ...m,
                  content: finalContent ?? m.content,
                  streaming: false,
                }
              : m,
          ),
        })),

      setThinking: (active) =>
        set((s) => ({
          isThinking: active,
          thinkingText: active ? s.thinkingText : '',
        })),

      appendThinkingDelta: (delta) =>
        set((s) => ({ thinkingText: s.thinkingText + delta })),

      clearThinking: () => set({ isThinking: false, thinkingText: '' }),

      setStreaming: (active) => set({ isStreaming: active }),

      setProposalText: (text) => set({ proposalText: text }),

      setAuthorId: (id) => set({ authorId: id }),

      setError: (err) => set({ error: err }),

      reset: () =>
        set({
          sessionId: null,
          phase: 'exploring_subject',
          messages: [],
          isStreaming: false,
          isThinking: false,
          thinkingText: '',
          proposalText: null,
          authorId: null,
          error: null,
        }),
    }),
    {
      name: 'author-commissioning-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sessionId: state.sessionId,
        phase: state.phase,
        messages: state.messages,
        proposalText: state.proposalText,
        authorId: state.authorId,
      }),
    },
  ),
)
